import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { AnimatePresence, motion, springs, useReducedMotion } from "../../../../lib/motion/index.js";
import { useI18n } from "../../../../lib/useI18n.js";

interface Props {
  value: string;
  onChange: (v: string) => void;
  /** Create mode: open the keyboard with the cursor in the title immediately. */
  autoFocus: boolean;
  /** Bumps every sheet open, so an exiting/reused sheet still re-enters edit mode. */
  focusKey: number;
  /** Topic / custom color tint applied to the committed (small, bold) title. */
  accent?: string | null;
  /** Fired after a non-empty title commits from editable input to display text. */
  onCommit?: () => void;
  onEditingChange?: (editing: boolean) => void;
}

/**
 * Title input that morphs on commit: while editing it's a full-height input;
 * when the keyboard is dismissed with a non-empty title the outline disappears,
 * the text shrinks + bolds and the field collapses — freeing space for the
 * options below (which slide up via the parent's layout animation). Tapping the
 * committed title re-opens the editable input.
 */
export function TitleField({ value, onChange, autoFocus, focusKey, accent, onCommit, onEditingChange }: Props) {
  const { t } = useI18n();
  const reduce = useReducedMotion();
  const [editing, setEditing] = useState(autoFocus || value.trim() === "");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    onEditingChange?.(editing);
  }, [editing, onEditingChange]);

  useEffect(() => {
    setEditing(autoFocus || value.trim() === "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFocus, focusKey]);

  useLayoutEffect(() => {
    if (!editing) return;
    inputRef.current?.focus({ preventScroll: true });
    const id = requestAnimationFrame(() => inputRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(id);
  }, [editing]);

  const commit = () => {
    if (!value.trim()) return;
    setEditing(false);
    onCommit?.();
  };

  // A stable min-height across both states: committing (input → small bold text)
  // must NOT shrink the row, so the sheet doesn't appear to jump/resize when the
  // keyboard dismisses.
  return (
    <motion.div
      data-sheet-title
      layout={!reduce}
      transition={reduce ? { duration: 0 } : springs.snappy}
      className="flex min-h-[3rem] items-center"
    >
      <AnimatePresence initial={false} mode="wait">
        {editing ? (
          <motion.input
            key="input"
            ref={inputRef}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); (e.target as HTMLInputElement).blur(); } }}
            placeholder={t("tasks.titleColumn")}
            aria-label={t("tasks.titleColumn")}
            initial={reduce ? false : { opacity: 0, y: 2, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -2, scale: 0.98 }}
            transition={reduce ? { duration: 0 } : springs.snappy}
            // Transparent so the topic color wash (the top band behind the content)
            // shows through the title field, not just around it.
            className="w-full rounded-lg border bg-transparent px-3 py-2.5 text-lg outline-none placeholder:text-muted-foreground focus:ring-2 focus:ring-ring"
          />
        ) : (
          <motion.button
            key="button"
            type="button"
            onClick={() => setEditing(true)}
            initial={reduce ? false : { opacity: 0, y: 2, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: -2, scale: 0.98 }}
            transition={reduce ? { duration: 0 } : springs.snappy}
            className="block w-full truncate px-1 text-left text-lg font-bold"
            style={accent ? { color: accent } : {}}
          >
            {value.trim() || t("agenda.untitled")}
          </motion.button>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
